import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { Banner, ErrorState, PageHead, SkeletonList } from "@ui/react";
import {
  CalendarIcon,
  ChevronRightIcon,
  DocumentIcon,
  PlusIcon,
  SettingsIcon,
  ToolsIcon,
} from "@ui/react/icons";
import { getDirectBookingSettings, listExtras, listServices } from "../../lib/api/contractor";
import type {
  ContractorService,
  DirectBookingSettings,
  ServiceExtra,
} from "../../lib/types/models";
import { SetupBeforeSwitchBanner, useBookableMode } from "./ModeGate";
import { errorMessage } from "../../lib/errorMessage";

interface Hints {
  services: ContractorService[] | null;
  extras: ServiceExtra[] | null;
  settings: DirectBookingSettings | null;
}

const EMPTY: Hints = { services: null, extras: null, settings: null };

export function BookingSettingsHubScreen() {
  const bookable = useBookableMode();
  const [state, setState] = useState<{ loading: boolean; error: unknown; data: Hints }>({
    loading: true,
    error: null,
    data: EMPTY,
  });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let ignore = false;
    setState((s) => ({ ...s, loading: true, error: null }));

    // allSettled, not all: this page is navigation first and a status readout
    // second, so one failing count must not lock the contractor out of the
    // screens themselves. Only a total failure is worth an error state.
    void Promise.allSettled([listServices(), listExtras(), getDirectBookingSettings()]).then(
      ([services, extras, settings]) => {
        if (ignore) return;
        const allFailed =
          services.status === "rejected" &&
          extras.status === "rejected" &&
          settings.status === "rejected";
        setState({
          loading: false,
          error: allFailed ? services.reason : null,
          data: {
            services: services.status === "fulfilled" ? services.value : null,
            extras: extras.status === "fulfilled" ? extras.value : null,
            settings: settings.status === "fulfilled" ? settings.value : null,
          },
        });
      },
    );

    return () => {
      ignore = true;
    };
  }, [reloadKey]);

  const retry = useCallback(() => setReloadKey((key) => key + 1), []);


  const { services, extras, settings } = state.data;
  const openDays = settings?.weeklyAvailability?.days?.filter(
    (day) => day.enabled && day.windows.length > 0,
  ).length;

  return (
    <div className="stack stack--loose">
      <PageHead
        title="Booking setup"
        lead="Everything a homeowner sees when they book you directly: what you offer, when you work and the rules around it."
      />
      {!bookable ? <SetupBeforeSwitchBanner /> : null}

      {state.loading ? (
        <div role="status">
          <span className="sr-only">Loading your booking setup</span>
          <SkeletonList rows={3} height="6rem" />
        </div>
      ) : state.error ? (
        <ErrorState message={errorMessage(state.error)} onRetry={retry} />
      ) : (
        <>
          {services && services.length === 0 ? (
            <Banner
              tone="warn"
              title="Customers can't book you yet"
              action={
                <Link className="btn btn-secondary" to="/settings/services/new">
                  Add a service
                </Link>
              }
            >
              You need at least one service with a price and a duration before your calendar
              can take bookings.
            </Banner>
          ) : null}

          <div className="card-grid">
            <HubCard
              to="/settings/services"
              icon={<ToolsIcon />}
              title="Services"
              description="What you offer, how long each job takes and what it costs."
              hint={
                services
                  ? services.length === 0
                    ? "No services yet"
                    : `${services.filter((service) => service.enabled).length} of ${services.length} enabled`
                  : null
              }
            />
            <HubCard
              to="/settings/extras"
              icon={<PlusIcon />}
              title="Add-ons"
              description="Optional extras a customer can attach to a booking."
              hint={
                extras
                  ? extras.length === 0
                    ? "No add-ons yet"
                    : `${extras.filter((extra) => extra.enabled).length} of ${extras.length} enabled`
                  : null
              }
            />
            <HubCard
              to="/settings/availability"
              icon={<CalendarIcon />}
              title="Availability"
              description="The hours you work each week, plus days you're closed."
              hint={
                openDays === undefined
                  ? null
                  : openDays === 0
                    ? "No days open yet"
                    : `Open ${openDays} ${openDays === 1 ? "day" : "days"} a week`
              }
            />
            <HubCard
              to="/settings/booking-policy"
              icon={<DocumentIcon />}
              title="Booking rules"
              description="Notice you need, gaps between jobs, how far ahead people can book, and your cancellation terms."
              hint={
                settings
                  // Capped at 7: the API already clamps via EffectiveHorizonDays,
                  // but Hosting and Cloud Run deploy independently, so this site
                  // can briefly talk to an API build that predates that clamp —
                  // the summary shouldn't promise a horizon the booking flow
                  // won't honour.
                  ? `${settings.bookingLeadTimeHours ?? 0}h notice · ${Math.min(settings.bookingHorizonDays ?? 0, 7)} days ahead`
                  : null
              }
            />
            <HubCard
              to="/profile"
              icon={<SettingsIcon />}
              title="Operating mode"
              description="Switch between taking direct bookings and quoting on marketplace jobs."
              hint={bookable ? "Taking direct bookings" : "Quoting on marketplace jobs"}
            />
          </div>
        </>
      )}
    </div>
  );
}

function HubCard({
  to,
  icon,
  title,
  description,
  hint,
}: {
  to: string;
  icon: ReactNode;
  title: string;
  description: string;
  hint: string | null;
}) {
  return (
    <Link to={to} className="row">
      <div className="stack stack--tight">
        <div className="cluster cluster--between">
          <span className="cluster cluster--tight">
            {icon}
            <span className="row__title">{title}</span>
          </span>
          <ChevronRightIcon />
        </div>
        <span className="muted">{description}</span>
        {hint ? <span className="dim label-sm">{hint}</span> : null}
      </div>
    </Link>
  );
}
